import { showCardCapture } from './payment.js';
import * as state          from './checkout-state.js';

// Single entry point for the checkout page. Owns two responsibilities that
// share a single input (the per-dialog confirm events):
//   1. UI fill — swap a delivery action's button for its summary; populate
//      the billing recipient panel from the confirmed billing address.
//   2. Place Order gating — enable the button only when every registered
//      gate (delivery actions, billing, card) reports complete. T&C is NOT
//      gated here; it's intercepted at click time in wireTermsCheck so the
//      customer can attempt the action and see the inline error.
//
// Each confirm-event listener does both jobs: fills the UI and calls
// state.set(...). The Place Order gate is driven by a single
// state.subscribe(...) — no separate updateGate() function.
export function initCheckout() {
  const placeOrderBtn = document.getElementById('place-order-button');

  // ── Register gates + seed initial state from server-rendered DOM ─────────

  // One entry per required delivery action. Server may have pre-rendered the
  // action's summary visible and stamped data-configured="true" (returning
  // customer / saved-cart state) — that's the bootstrap signal that the
  // action arrived complete.
  document.querySelectorAll('.checkout__action[data-required]').forEach(action => {
    state.register(action);
    if (action.dataset.configured === 'true') state.set(action, true);
  });

  // Recipients buttons can later be rewritten into the partial-progress
  // shape (two spans) or hidden behind a summary — capture the plain
  // "Add Recipients" label up front so a group that's dropped back to zero
  // allocated (every recipient removed) can be restored to it exactly,
  // rather than hardcoding the string here.
  document.querySelectorAll('.checkout__action[data-required] button.btn[data-quantity]').forEach(button => {
    button.dataset.defaultLabel = button.textContent.trim();
  });

  // Billing and card. Billing is complete when the saved-billing panel is
  // visible at page load (the returning-customer case). Card always starts
  // false — the Square form fires payment:card-complete when it's valid.
  state.register('billing');
  state.register('card');
  const billingPanel = document.querySelector('.checkout__payment-billing-address-details');
  if (billingPanel && !billingPanel.hidden) state.set('billing', true);

  // ── Drive the Place Order button from state ──────────────────────────────
  // Disabled until every gate (delivery actions, billing, card) reports
  // complete; while disabled the label reads "More Information Required"
  // so the customer knows the button is intentionally blocked. The
  // ready-state label is captured from the markup so this code doesn't
  // have to hardcode "Place Order".

  if (placeOrderBtn) {
    const labelReady      = (placeOrderBtn.textContent.trim() || 'Place Order');
    const labelIncomplete = 'More Information Required';
    const applyState = () => {
      const ready = state.isComplete();
      placeOrderBtn.disabled    = !ready;
      placeOrderBtn.textContent = ready ? labelReady : labelIncomplete;
    };
    state.subscribe(applyState);
    applyState();
  }

  // ── Confirm event listeners ──────────────────────────────────────────────

  document.addEventListener('calendar:confirm', e => {
    fillAction(e.target, e.detail.formatted, e.detail.date);
    const action = e.target.closest('.checkout__action');
    if (action) state.set(action, true);
  });

  document.addEventListener('occasion-name:confirm', e => {
    fillAction(e.target, e.detail.value, e.detail.value);
    const action = e.target.closest('.checkout__action');
    if (action) state.set(action, true);
  });

  document.addEventListener('address-form:confirm', e => {
    const payment = e.target.closest('.checkout__payment');
    if (payment) {
      fillRecipient(payment, e.detail, e.target);
      showCardCapture(e.detail);
      state.set('billing', true);
      return;
    }
    // Picks bubbling through the recipients dialog are handled by recipients.js
    if (e.target.closest('.rcp-modal')) return;
    fillAction(e.target, e.detail.formattedOneLine, e.detail.id);
    const action = e.target.closest('.checkout__action');
    if (action) state.set(action, true);
  });

  document.addEventListener('recipients:confirm', e => {
    // A Multi-Address group is fully configured only when every bought item
    // has been allocated to a recipient. The trigger carries the group's
    // capacity in `data-quantity`; the detail carries the saved recipients
    // and their quantities.
    const capacity   = parseInt(e.target.dataset.quantity || '0', 10) || 0;
    const allocated  = (e.detail.recipients || [])
      .reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
    const unitPlural = e.target.dataset.unitPlural || 'items';
    const fullyAllocated = capacity > 0 && allocated >= capacity;

    const action = e.target.closest('.checkout__action');
    if (!action) return;

    if (fullyAllocated) {
      const label = `${e.detail.count} ${e.detail.count === 1 ? 'Recipient' : 'Recipients'}`;
      showRecipientsSummary(action, label);
    } else if (allocated === 0) {
      // Every recipient was removed (see recipients.js's pending-removal
      // flow) — back to square one, so the trigger should look exactly
      // like it did before anyone was ever added, not show "0 of 13
      // allocated" progress for a group with nothing in it.
      showRecipientsInitial(action);
    } else {
      // Partial: button stays visible with progress on the left and
      // "Complete" on the right — clicking it reopens the dialog to top up.
      showRecipientsProgress(action, allocated, capacity, unitPlural);
    }
    state.set(action, fullyAllocated);
  });

  document.addEventListener('payment:card-complete', e => {
    state.set('card', e.detail?.complete !== false);
  });

  // ── Terms & conditions + returning customer ──────────────────────────────

  wireTermsCheck(placeOrderBtn);
  bootstrapSavedBilling();
}

// ── DOM update helpers ──────────────────────────────────────────────────────

function fillAction(opener, displayText, rawValue) {
  const action = opener.closest('.checkout__action');
  if (!action) return;

  const button   = action.querySelector('button.btn');
  const summary  = action.querySelector('.checkout__action-summary');
  const valueEl  = summary?.querySelector('.checkout__action-summary-value span');
  const editLink = summary?.querySelector('.checkout__action-summary-edit');
  const input    = action.querySelector('input[type="hidden"]');

  if (button)   button.hidden = true;
  if (summary)  summary.hidden = false;
  if (valueEl)  valueEl.textContent = displayText;
  if (editLink) {
    editLink.dataset.savedId = rawValue;
    // Carry the save endpoint forward onto the Edit trigger too, otherwise
    // re-editing an already-selected address has nowhere to POST to and the
    // edit silently gets dropped (address-form.js gates its save on
    // activeOpener.dataset.saveUrl).
    if (opener.dataset.saveUrl) editLink.dataset.saveUrl = opener.dataset.saveUrl;
  }
  if (input)    input.value = rawValue;
}

function fillRecipient(payment, detail, opener) {
  const fields    = payment.querySelector('.checkout__payment-billing-fields');
  const recipient = payment.querySelector('.checkout__payment-billing-address-details');
  const nameEl    = recipient?.querySelector('.checkout__payment-billing-name');
  const addressEl = recipient?.querySelector('.checkout__payment-billing-address');
  const editLink  = recipient?.querySelector('.checkout__payment-billing-edit');

  if (fields)    fields.hidden = true;
  if (recipient) recipient.hidden = false;
  if (nameEl)    nameEl.textContent = [detail.firstName, detail.lastName].filter(Boolean).join(' ');
  if (addressEl) addressEl.textContent = detail.formattedOneLine;
  if (editLink) {
    editLink.dataset.savedId = detail.id;
    if (opener?.dataset.saveUrl) editLink.dataset.saveUrl = opener.dataset.saveUrl;
  }
}

function showRecipientsSummary(action, label) {
  const button  = action.querySelector('button.btn');
  const summary = action.querySelector('.checkout__action-summary');
  const valueEl = summary?.querySelector('.checkout__action-summary-value span');

  if (button) {
    button.hidden = true;
    button.classList.remove('is-partial');
  }
  if (summary) summary.hidden = false;
  if (valueEl) valueEl.textContent = label;
}

function showRecipientsInitial(action) {
  const button  = action.querySelector('button.btn');
  const summary = action.querySelector('.checkout__action-summary');

  if (summary) summary.hidden = true;
  if (button) {
    button.hidden = false;
    button.classList.remove('is-partial');
    button.textContent = button.dataset.defaultLabel || 'Add Recipients';
  }
}

function showRecipientsProgress(action, allocated, capacity, unitPlural) {
  const button  = action.querySelector('button.btn');
  const summary = action.querySelector('.checkout__action-summary');

  if (summary) summary.hidden = true;
  if (button) {
    button.hidden = false;
    button.classList.add('is-partial');
    button.replaceChildren(
      makeSpan('checkout__action-btn-progress', `${allocated} of ${capacity} ${unitPlural} allocated`),
      makeSpan('checkout__action-btn-complete', 'Add Recipients'),
    );
  }
}

function makeSpan(className, text) {
  const span = document.createElement('span');
  span.className   = className;
  span.textContent = text;
  return span;
}

// ── Terms & conditions ──────────────────────────────────────────────────────
// We don't gate the Place Order button on the checkbox (so the user can
// attempt the action and see the inline error). Capture-phase click handler
// runs before payment.js's bubble-phase tokenise handler — if the box isn't
// ticked we stop propagation and surface the error.

function wireTermsCheck(placeOrderBtn) {
  const termsWrap     = document.querySelector('.order-summary__terms');
  const termsCheckbox = termsWrap?.querySelector('.order-summary__terms-input');
  const errorEl       = termsWrap?.querySelector('.order-summary__terms-error');
  if (!termsWrap || !termsCheckbox || !placeOrderBtn) return;

  function clearError() {
    termsWrap.classList.remove('is-invalid');
    if (errorEl) errorEl.hidden = true;
  }
  function showError() {
    termsWrap.classList.add('is-invalid');
    if (errorEl) errorEl.hidden = false;
  }

  termsCheckbox.addEventListener('change', () => {
    if (termsCheckbox.checked) clearError();
  });

  placeOrderBtn.addEventListener('click', e => {
    if (!termsCheckbox.checked) {
      e.preventDefault();
      e.stopImmediatePropagation();
      showError();
      termsCheckbox.focus();
    }
  }, true);
}

// ── Returning customer ──────────────────────────────────────────────────────
// The server has pre-rendered the billing recipient panel + a JSON blob of
// the saved address. Mount the Square card form immediately, same as we
// would after a fresh address-form:confirm. The billing gate has already
// been seeded by the init block above (panel is visible).

function bootstrapSavedBilling() {
  const details = document.querySelector('.checkout__payment-billing-address-details');
  if (!details || details.hidden) return;

  const dataEl = details.querySelector('script.checkout__payment-billing-data');
  const raw = dataEl?.textContent.trim();
  if (!raw) return;

  let billing;
  try {
    billing = JSON.parse(raw);
  } catch (err) {
    console.error('Invalid pre-rendered billing JSON', err);
    return;
  }
  if (billing?.firstName) showCardCapture(billing);
}
